import { ajax } from 'rxjs/ajax';
import { map, mapTo } from 'rxjs/operators';
import User from 'Js/api/v1/model/User';
import Image from 'Js/api/v1/model/Image';

export default class Reply {
    constructor({
        id,
        parentReplyId,
        targetReplyId,
        url,
        author,
        creationDate,
        body,
        files,
        childrenCount,
        lastChild,
        isDeleted
    }) {
        /**
         * Идентификатор ответа
         * @type {number}
         */
        this.id = id;
        /**
         * Идентификатор ветки обсуждения, в которую входит данный ответ
         * (ветка обсуждения начинается с любого ответа без parentReplyId)
         * @type {number}
         */
        this.parentReplyId = parentReplyId;
        /**
         * Идентификатор другого ответа, на который дан данный ответ
         * (при этом ответ ответ входит в ветку обуждения и parentReplyId может отличаться)
         * @type {number}
         */
        this.targetReplyId = targetReplyId;
        /**
         * Ссылка на ответ (от корня форума)
         * @type {string}
         * @todo у вложенных комментариев (lastChild) не заполнен url, исправить на бекенде
         * @todo убрать замыкающий слеш в адресе ответа, формируемом бекендом
         */
        this.url = (url || '').replace('/#', '#');
        /**
         * Автор ответа
         * @type {User}
         */
        this.author = author;
        /**
         * Дата ответа
         * @type {Date}
         */
        this.creationDate = creationDate;
        /**
         * Текст ответа
         * @type {string}
         */
        this.body = body;
        /**
         * Прикреплённые к ответу файлы
         * (могут быть разных типов, но парсятся как Image, чтобы сформировать превью)
         * @type {Image[]}
         */
        this.files = files.sort((f1, f2) => f1.id - f2.id);
        /**
         * Количество комментариев к ответу
         * @type {number}
         */
        this.childrenCount = childrenCount;
        /**
         * Последний комментарий к ответу
         * @type {Reply}
         */
        this.lastChild = lastChild;
        /**
         * Ответ удалён (но привилегированный пользователь может его видеть)
         * @type {boolean}
         */
        this.isDeleted = isDeleted;
    }

    static parse(DTO) {
        if (DTO && typeof DTO == 'object') {
            const {
                Id,
                ParentReplyId,
                AnswerToReplyId,
                Url,
                Author,
                CreationDate,
                IsDeleted,
                Message,
                Files,
                Comments
            } = DTO;

            return new Reply({
                id: Id,
                url: Url,
                parentReplyId: ParentReplyId,
                targetReplyId: AnswerToReplyId,
                author: User.parse(Author),
                creationDate: new Date(CreationDate),
                body: Message,
                files: Files.map(Image.parse),
                childrenCount: Comments ? Comments.CommentsCount : 0,
                lastChild: Comments ? Reply.parse(Comments.LastComment) : null,
                isDeleted: IsDeleted
            });
        }
        return null;
    }

    /**
     * Создаёт новый экземпляр Reply,
     * заполненный данными из исходного объекта
     * @param {Object} reply
     * @returns {Topic}
     */
    static create(reply) {
        const replyDefaults = {
            parentReplyId: 0,
            targetReplyId: 0,
            body: '',
            files: [],
            childrenCount: 0,
            isDeleted: false
        };
        return new Reply({ ...replyDefaults, ...reply });
    }

    /**
     * Загружает список ответов на тему
     * @param {number} topicId
     * @param {Object} param1
     * @param {number} [param1.offset]
     * @param {number} [param1.limit]
     * @returns {Observable}
     */
    static getTopicReplies(topicId, { offset = 0, limit = 30 }) {
        return ajax
            .get(
                `/api/v1/forum/topics/${topicId}/replies?offset=${offset}&limit=${limit}`
            )
            .pipe(
                map(({ xhr, response: results }) => ({
                    results: results.map(Reply.parse),
                    offset,
                    limit,
                    totalCount: parseInt(
                        xhr.getResponseHeader('X-Total-Count'),
                        10
                    )
                }))
            );
    }

    /**
     * Загружает список сообщений пользователя
     * @param {number} userId
     * @param {Object} param1
     * @param {number} [param1.offset]
     * @param {number} [param1.limit]
     * @returns {Observable}
     */
    static getUserReplies(userId, { offset = 0, limit = 30 }) {
        return ajax
            .get(
                `/api/v1/forum/users/${userId}/replies?offset=${offset}&limit=${limit}`
            )
            .pipe(
                map(({ xhr, response: results }) => ({
                    results: results.map(Reply.parse),
                    offset,
                    limit,
                    totalCount: parseInt(
                        xhr.getResponseHeader('X-Total-Count'),
                        10
                    )
                }))
            );
    }

    /**
     * Загружает все комментарии к ответу
     * @param {number} replyId
     * @returns {Observable}
     */
    static getReplyComments(replyId) {
        return (
            ajax
                .getJSON(`/api/v1/forum/replies/${replyId}/comments`)
                // В ответ приходит ForumReplyCommentsDTO с полями
                // Comments, CommentsCount, LastComment.
                // Нас интересует только Comments.
                .pipe(map(({ Comments: replies }) => replies.map(Reply.parse)))
        );
    }

    /**
     * Удаляет сообщение
     * @param {number} replyId
     * @returns {Observable}
     */
    static delete(replyId) {
        return ajax
            .delete(`/api/v1/forum/replies/${replyId}`)
            .pipe(mapTo(replyId));
    }

    /**
     * Ability configuration
     * @see https://casl.js.org/v5/en/guide/subject-type-detection
     */
    static get modelName() {
        return 'Reply';
    }

    /**
     * Создаёт ответ на тему, или комментарий к другому ответу
     * @param {number} topicId
     * @param {number} [parentReplyId]
     * @returns {Observable}
     */
    post(topicId, parentReplyId = 0) {
        const data = new FormData();

        data.append('reply', JSON.stringify(this));

        this.files.forEach(({ blob }) => {
            if (blob) {
                data.append('files', blob);
            }
        });

        // prettier-ignore
        if (parentReplyId) {
            return ajax
                .post(`/api/v1/forum/replies/${parentReplyId}/comments?topicId=${topicId}`, data)
                .pipe(map(({ response }) => Reply.parse(response)));
        } else {
            return ajax
                .post(`/api/v1/forum/topics/${topicId}/replies`, data)
                .pipe(map(({ response }) => Reply.parse(response)));
        }
    }

    /**
     * Редактирует сообщение
     * @param {File[]} delFiles - удаляемые файлы
     * @returns {Observable}
     */
    patch(delFiles = []) {
        const data = new FormData();
        const delFilesIds = delFiles.map(f => f.id).filter(v => v);

        data.append('reply', JSON.stringify(this));

        this.files.forEach(({ blob }) => {
            if (blob) {
                data.append('newFiles', blob);
            }
        });

        return ajax
            .patch(
                delFilesIds.length > 0
                    ? `/api/v1/forum/replies/${this.id}?delFiles=${delFilesIds}`
                    : `/api/v1/forum/replies/${this.id}`,
                data
            )
            .pipe(map(({ response: reply }) => Reply.parse(reply)));
    }

    /**
     * Удаляет конкретный файл из прикреплённых к теме
     * @param {File} file
     * @returns {Observable}
     */
    deleteFile(file) {
        return ajax
            .delete(`/api/v1/forum/replies/${this.id}/files/${file.id}`)
            .pipe(
                mapTo(
                    Reply.create({
                        ...this,
                        files: this.files.filter(f => f.id != file.id)
                    })
                )
            );
    }

    toJSON() {
        return {
            Message: this.body,
            ReplyId: this.parentReplyId,
            AnswerToReplyId: this.targetReplyId
        };
    }

    toString() {
        return this.body;
    }
}
